import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import LanguageToggle from "./LanguageToggle";
import MobileMenu from "./MobileMenu";
import Button from "../../ui/Button";

// نگاشت لینک‌های nav → کلید در آبجکت sections
const NAV_LINKS = [
  { key: "why", target: "why", anchor: "#why" },
  { key: "benefits", target: "botBenefits", anchor: "#botBenefits" },
  { key: "activeBots", target: "activeBots", anchor: "#activeBots" },
  { key: "pricing", target: "landingPlans", anchor: "#landingPlans" },
  { key: "articles", target: "articles", anchor: "#articles" },
];

const NAVBAR_OFFSET = 90; // ارتفاع navbar + کمی فاصله

const Navbar = ({ sections }) => {
  const { t } = useTranslation("landing");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, link) => {
    // اگه ref وجود داشت، اسکرول نرم با offset
    const ref = sections?.[link.target];
    if (ref?.current) {
      e.preventDefault();
      const top =
        ref.current.getBoundingClientRect().top +
        window.scrollY -
        NAVBAR_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
    }
    // در غیر این صورت، behavior پیش‌فرض anchor کار می‌کنه
  };

  return (
    <>
      <nav
        className={clsx(
          "sticky top-0 z-50 backdrop-blur-xl backdrop-saturate-150 bg-glass",
          "border-b transition-all duration-300",
          "transform-gpu will-change-transform",
          scrolled ? "border-line shadow-card" : "border-transparent"
        )}
      >
        <div className="mx-auto flex h-[78px] max-w-container items-center justify-between gap-3 px-5 sm:px-7">
          <Logo />

          {/* Desktop links */}
          <div className="hidden items-center gap-8 text-[15px] font-medium text-ink-soft lg:!flex xl:gap-9">
            {NAV_LINKS.map((link) => (
              <a
                key={link.key}
                href={link.anchor}
                onClick={(e) => handleNavClick(e, link)}
                className="group relative py-1.5 transition-colors hover:text-teal-2 dark:hover:text-teal-3"
              >
                {t(`nav.${link.key}`)}
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 start-0 h-0.5 w-0 rounded-full bg-gradient-to-r from-teal to-teal-2 transition-all duration-300 group-hover:w-full"
                />
              </a>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            <LanguageToggle />
            <ThemeToggle />

            {/* Login — desktop only */}
            <Button
              as={Link}
              to="/login"
              variant="ghost"
              size="sm"
              className="hidden lg:!inline-flex"
            >
              {t("nav.login")}
            </Button>

            {/* Start Free — hidden on very small screens */}
            <Button
              as={Link}
              to="/login"
              variant="primary"
              size="sm"
              className="hidden sm:inline-flex"
            >
              {t("nav.start")}
            </Button>

            {/* Burger */}
            <button
              type="button"
              aria-label={t("a11y.toggleMenu")}
              aria-expanded={menuOpen}
              aria-controls="landing-mobile-menu"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex flex-col gap-[5px] rounded-lg p-2.5 transition-colors hover:bg-surface-2 lg:!hidden"
            >
              <span
                className={clsx(
                  "h-0.5 w-[22px] rounded-sm bg-ink transition-all duration-300",
                  menuOpen && "translate-y-[7px] rotate-45"
                )}
              />
              <span
                className={clsx(
                  "h-0.5 w-[22px] rounded-sm bg-ink transition-all duration-300",
                  menuOpen && "opacity-0"
                )}
              />
              <span
                className={clsx(
                  "h-0.5 w-[22px] rounded-sm bg-ink transition-all duration-300",
                  menuOpen && "-translate-y-[7px] -rotate-45"
                )}
              />
            </button>
          </div>
        </div>
      </nav>

      <div id="landing-mobile-menu">
        <MobileMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          links={NAV_LINKS}
          sections={sections}
        />
      </div>
    </>
  );
};

export default Navbar;